import * as React from 'react';
import PropTypes from 'prop-types';
import { useDataMode } from '../../context/DataModeContext';
import { getDemoData } from '../../data/demoData';

// Stage definitions
export const GROW_STAGES = {
  seedling: { id: 'seedling', label: 'Seedling/Clone', order: 1 },
  vegetative: { id: 'vegetative', label: 'Vegetative', order: 2 },
  flower: { id: 'flower', label: 'Flower', order: 3 },
  lateFlower: { id: 'lateFlower', label: 'Mid-Late Flower', order: 4 },
};

export const STAGE_ORDER = ['seedling', 'vegetative', 'flower', 'lateFlower'];

// Default stage configuration template
const createDefaultStageConfig = (duration, sensorOverrides = {}) => ({
  duration, // days
  sensors: {
    ph: { optimalMin: 5.8, optimalMax: 6.5 },
    ec: { optimalMin: 1.2, optimalMax: 2.0 },
    tds: { optimalMin: 600, optimalMax: 1000 },
    waterTemp: { optimalMin: 65, optimalMax: 72 },
    orp: { optimalMin: 300, optimalMax: 500 },
    airTemp: { optimalMin: 72, optimalMax: 82 },
    humidity: { optimalMin: 50, optimalMax: 70 },
    co2: { optimalMin: 800, optimalMax: 1200 },
    vpd: { optimalMin: 0.8, optimalMax: 1.2 },
    ppfd: { optimalMin: 600, optimalMax: 1000 },
    dli: { optimalMin: 30, optimalMax: 50 },
    ...sensorOverrides,
  },
});

// Default preset: General Indoor Grow
const DEFAULT_PRESETS = [
  {
    id: 'default-indoor',
    name: 'General Indoor Grow',
    description: 'Balanced settings suitable for most indoor plants',
    stages: {
      seedling: createDefaultStageConfig(14, {
        ph: { optimalMin: 5.8, optimalMax: 6.2 },
        ec: { optimalMin: 0.5, optimalMax: 0.8 },
        tds: { optimalMin: 250, optimalMax: 400 },
        airTemp: { optimalMin: 75, optimalMax: 80 },
        humidity: { optimalMin: 65, optimalMax: 70 },
        ppfd: { optimalMin: 200, optimalMax: 400 },
        dli: { optimalMin: 12, optimalMax: 20 },
      }),
      vegetative: createDefaultStageConfig(28, {
        ph: { optimalMin: 5.8, optimalMax: 6.5 },
        ec: { optimalMin: 1.0, optimalMax: 1.6 },
        tds: { optimalMin: 500, optimalMax: 800 },
        airTemp: { optimalMin: 72, optimalMax: 82 },
        humidity: { optimalMin: 50, optimalMax: 70 },
        ppfd: { optimalMin: 400, optimalMax: 600 },
        dli: { optimalMin: 25, optimalMax: 35 },
      }),
      flower: createDefaultStageConfig(35, {
        ph: { optimalMin: 6.0, optimalMax: 6.5 },
        ec: { optimalMin: 1.4, optimalMax: 2.0 },
        tds: { optimalMin: 700, optimalMax: 1000 },
        airTemp: { optimalMin: 68, optimalMax: 78 },
        humidity: { optimalMin: 40, optimalMax: 50 },
        ppfd: { optimalMin: 600, optimalMax: 900 },
        dli: { optimalMin: 35, optimalMax: 45 },
      }),
      lateFlower: createDefaultStageConfig(21, {
        ph: { optimalMin: 6.0, optimalMax: 6.5 },
        ec: { optimalMin: 1.0, optimalMax: 1.6 },
        tds: { optimalMin: 500, optimalMax: 800 },
        airTemp: { optimalMin: 65, optimalMax: 75 },
        humidity: { optimalMin: 30, optimalMax: 40 },
        ppfd: { optimalMin: 600, optimalMax: 800 },
        dli: { optimalMin: 30, optimalMax: 40 },
      }),
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// Local storage keys
const STORAGE_KEY = 'mygrow_grow_presets';
const GROWS_KEY = 'mygrow_grows';
const ACTIVE_GROW_ID_KEY = 'mygrow_active_grow_id';

const GrowContext = React.createContext(null);

export function GrowProvider({ children }) {
  // Call hook unconditionally at the top level
  // (useDataMode will provide null if DataModeProvider is not in the tree)
  const dataModeContext = useDataMode();

  const isDemo = dataModeContext?.isDemo || false;
  const storagePrefix = dataModeContext?.storagePrefix || '';

  // Get appropriate storage based on mode
  const getStorage = React.useCallback(() => {
    return isDemo ? sessionStorage : localStorage;
  }, [isDemo]);

  // Load from storage with mode-specific prefix and basic type validation
  const loadFromStorage = React.useCallback((key, defaultValue) => {
    try {
      const storage = getStorage();
      const stored = storage.getItem(storagePrefix + key);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Basic type validation: ensure parsed type matches default
        if (defaultValue !== undefined && defaultValue !== null) {
          if (Array.isArray(defaultValue) && !Array.isArray(parsed)) return defaultValue;
          if (typeof parsed !== typeof defaultValue) return defaultValue;
        }
        return parsed;
      }
    } catch (e) {
      console.error('Error loading from storage:', e);
    }
    return defaultValue;
  }, [getStorage, storagePrefix]);

  // Save to storage with mode-specific prefix
  const saveToStorage = React.useCallback((key, value) => {
    try {
      const storage = getStorage();
      storage.setItem(storagePrefix + key, JSON.stringify(value));
    } catch (e) {
      console.error('Error saving to storage:', e);
    }
  }, [getStorage, storagePrefix]);

  // Initialize state - for demo mode, always start fresh with demo data
  const getInitialData = React.useCallback(() => {
    if (isDemo) {
      // Demo mode: always start with fresh demo data
      const demoData = getDemoData();
      return {
        presets: demoData.presets,
        grows: demoData.grows,
        activeGrowId: demoData.activeGrowId,
      };
    } else {
      // Live mode: load from localStorage, fall back to defaults
      return {
        presets: loadFromStorage(STORAGE_KEY, DEFAULT_PRESETS),
        grows: loadFromStorage(GROWS_KEY, []),
        activeGrowId: loadFromStorage(ACTIVE_GROW_ID_KEY, null),
      };
    }
  }, [isDemo, loadFromStorage]);

  const initialData = React.useMemo(() => getInitialData(), [getInitialData]);

  const [presets, setPresets] = React.useState(initialData.presets);
  const [grows, setGrows] = React.useState(initialData.grows);
  const [activeGrowId, setActiveGrowId] = React.useState(initialData.activeGrowId);

  // Persist to storage (different behavior for demo vs live)
  React.useEffect(() => {
    saveToStorage(STORAGE_KEY, presets);
  }, [presets, saveToStorage]);

  React.useEffect(() => {
    saveToStorage(GROWS_KEY, grows);
  }, [grows, saveToStorage]);

  React.useEffect(() => {
    saveToStorage(ACTIVE_GROW_ID_KEY, activeGrowId);
  }, [activeGrowId, saveToStorage]);

  // Generate unique ID using cryptographically secure random values
  const generateId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for older browsers
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  };

  // ==================== PRESET MANAGEMENT ====================

  const addPreset = React.useCallback((name = 'New Preset') => {
    const newPreset = {
      id: generateId(),
      name,
      description: '',
      stages: {
        seedling: createDefaultStageConfig(14),
        vegetative: createDefaultStageConfig(28),
        flower: createDefaultStageConfig(35),
        lateFlower: createDefaultStageConfig(21),
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setPresets(prev => [...prev, newPreset]);
    return newPreset.id;
  }, []);

  const updatePreset = React.useCallback((presetId, updates) => {
    setPresets(prev => prev.map(preset =>
      preset.id === presetId
        ? { ...preset, ...updates, updatedAt: new Date().toISOString() }
        : preset
    ));
  }, []);

  const deletePreset = React.useCallback((presetId) => {
    setPresets(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter(preset => preset.id !== presetId);
    });
  }, []);

  const duplicatePreset = React.useCallback((presetId) => {
    const preset = presets.find(p => p.id === presetId);
    if (!preset) return null;

    const newPreset = {
      ...JSON.parse(JSON.stringify(preset)),
      id: generateId(),
      name: `${preset.name} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setPresets(prev => [...prev, newPreset]);
    return newPreset.id;
  }, [presets]);

  const copyStageSettings = React.useCallback((presetId, fromStage, toStage) => {
    setPresets(prev => prev.map(preset => {
      if (preset.id !== presetId) return preset;

      const fromConfig = preset.stages[fromStage];
      if (!fromConfig) return preset;

      return {
        ...preset,
        stages: {
          ...preset.stages,
          [toStage]: {
            ...JSON.parse(JSON.stringify(fromConfig)),
            duration: preset.stages[toStage]?.duration || fromConfig.duration,
          },
        },
        updatedAt: new Date().toISOString(),
      };
    }));
  }, []);

  const updateStageSettings = React.useCallback((presetId, stage, updates) => {
    setPresets(prev => prev.map(preset => {
      if (preset.id !== presetId) return preset;

      return {
        ...preset,
        stages: {
          ...preset.stages,
          [stage]: {
            ...preset.stages[stage],
            ...updates,
          },
        },
        updatedAt: new Date().toISOString(),
      };
    }));
  }, []);

  const updateSensorRange = React.useCallback((presetId, stage, sensorType, optimalMin, optimalMax) => {
    setPresets(prev => prev.map(preset => {
      if (preset.id !== presetId) return preset;

      return {
        ...preset,
        stages: {
          ...preset.stages,
          [stage]: {
            ...preset.stages[stage],
            sensors: {
              ...preset.stages[stage].sensors,
              [sensorType]: { optimalMin, optimalMax },
            },
          },
        },
        updatedAt: new Date().toISOString(),
      };
    }));
  }, []);

  // ==================== GROW MANAGEMENT ====================

  const addGrow = React.useCallback((name, presetId, startDate, startStage = 'seedling') => {
    const preset = presets.find(p => p.id === presetId);
    const startingStageIndex = STAGE_ORDER.indexOf(startStage);

    // Calculate days to skip for starting from a later stage
    let daysToSkip = 0;
    if (preset) {
      for (let i = 0; i < startingStageIndex; i++) {
        daysToSkip += preset.stages[STAGE_ORDER[i]]?.duration || 0;
      }
    }

    const actualStartDate = startDate instanceof Date ? startDate : new Date(startDate);
    const adjustedStartDate = new Date(actualStartDate);
    adjustedStartDate.setDate(adjustedStartDate.getDate() - daysToSkip);

    const newGrow = {
      id: generateId(),
      name: name || 'New Grow',
      presetId,
      startDate: adjustedStartDate.toISOString(),
      currentStage: startStage,
      status: 'active', // 'active' | 'cancelled' | 'completed'
      endDate: null,
      createdAt: new Date().toISOString(),
      // Grow traits and description
      traits: {
        environment: 'indoor', // 'indoor' | 'outdoor' | 'greenhouse'
        medium: 'hydroponic', // 'hydroponic' | 'soil' | 'coco' | 'aeroponics'
        areaSize: '',
        lightingType: 'LED',
        description: '',
      },
      // Reminders (recurring task timers)
      reminders: [],
      // Per-grow dashboard configuration
      dashboardConfig: {
        showOverview: true,
        showReadings: true,
        overviewSensors: ['ph', 'airTemp', 'humidity'],
        sections: [
          {
            id: 'reservoir-' + generateId(),
            title: 'Water Reservoir',
            sensors: [
              { id: 'ph-1', type: 'ph', name: 'pH Level', size: 'small', dataPath: 'sensors/reservoir/ph', color: '#3b82f6' },
              { id: 'ec-1', type: 'ec', name: 'EC', size: 'small', dataPath: 'sensors/reservoir/ec', color: '#f59e0b' },
              { id: 'tds-1', type: 'tds', name: 'TDS', size: 'small', dataPath: 'sensors/reservoir/tds', color: '#8b5cf6' },
              { id: 'waterTemp-1', type: 'waterTemp', name: 'Water Temp', size: 'small', dataPath: 'sensors/reservoir/temp', color: '#ef4444' },
            ],
          },
          {
            id: 'atmosphere-' + generateId(),
            title: 'Atmosphere',
            sensors: [
              { id: 'airTemp-1', type: 'airTemp', name: 'Air Temp', size: 'small', dataPath: 'sensors/atmosphere/temp', color: '#ef4444' },
              { id: 'humidity-1', type: 'humidity', name: 'Humidity', size: 'small', dataPath: 'sensors/atmosphere/humidity', color: '#3b82f6' },
              { id: 'co2-1', type: 'co2', name: 'CO2', size: 'small', dataPath: 'sensors/atmosphere/co2', color: '#22c55e' },
            ],
          },
        ],
      },
    };

    setGrows(prev => [...prev, newGrow]);
    setActiveGrowId(newGrow.id);
    return newGrow.id;
  }, [presets]);

  const updateGrow = React.useCallback((growId, updates) => {
    setGrows(prev => prev.map(grow =>
      grow.id === growId ? { ...grow, ...updates } : grow
    ));
  }, []);

  const deleteGrow = React.useCallback((growId) => {
    setGrows(prev => prev.filter(grow => grow.id !== growId));
    if (activeGrowId === growId) {
      setActiveGrowId(null);
    }
  }, [activeGrowId]);

  // End a grow with specified status ('cancelled' or 'completed') or delete it
  const endGrow = React.useCallback((growId, action) => {
    if (action === 'delete') {
      // Completely remove the grow
      deleteGrow(growId);
    } else if (action === 'cancelled' || action === 'complete' || action === 'completed') {
      // Normalize 'complete' to 'completed' for consistency
      const normalizedStatus = action === 'complete' ? 'completed' : action;
      // Update status and set end date
      setGrows(prev => prev.map(grow =>
        grow.id === growId
          ? {
              ...grow,
              status: normalizedStatus,
              endDate: new Date().toISOString(),
            }
          : grow
      ));
      if (activeGrowId === growId) {
        setActiveGrowId(null);
      }
    }
  }, [activeGrowId, deleteGrow]);

  const updateGrowStage = React.useCallback((growId, stage) => {
    setGrows(prev => prev.map(grow =>
      grow.id === growId ? { ...grow, currentStage: stage } : grow
    ));
  }, []);

  // Update dashboard config for a specific grow
  const updateGrowDashboardConfig = React.useCallback((growId, configUpdates) => {
    setGrows(prev => prev.map(grow =>
      grow.id === growId
        ? {
            ...grow,
            dashboardConfig: {
              ...grow.dashboardConfig,
              ...configUpdates,
            },
          }
        : grow
    ));
  }, []);

  // Get dashboard config for a specific grow
  const getGrowDashboardConfig = React.useCallback((growId) => {
    const grow = grows.find(g => g.id === growId);
    return grow?.dashboardConfig || null;
  }, [grows]);

  // ==================== REMINDER MANAGEMENT ====================

  // Add a reminder to a specific grow
  const addReminder = React.useCallback((growId, reminder) => {
    const newReminder = {
      id: generateId(),
      label: reminder.label || 'Reminder',
      icon: reminder.icon || 'timer', // 'timer', 'water', 'clean', 'feed', 'check'
      intervalHours: reminder.intervalHours || 24,
      lastReset: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    };
    setGrows(prev => prev.map(grow =>
      grow.id === growId
        ? { ...grow, reminders: [...(grow.reminders || []), newReminder] }
        : grow
    ));
    return newReminder.id;
  }, []);

  // Update a reminder
  const updateReminder = React.useCallback((growId, reminderId, updates) => {
    setGrows(prev => prev.map(grow => {
      if (grow.id !== growId) return grow;
      return {
        ...grow,
        reminders: (grow.reminders || []).map(r =>
          r.id === reminderId ? { ...r, ...updates } : r
        ),
      };
    }));
  }, []);

  // Delete a reminder
  const deleteReminder = React.useCallback((growId, reminderId) => {
    setGrows(prev => prev.map(grow => {
      if (grow.id !== growId) return grow;
      return {
        ...grow,
        reminders: (grow.reminders || []).filter(r => r.id !== reminderId),
      };
    }));
  }, []);

  // Reset a reminder's timer
  const resetReminder = React.useCallback((growId, reminderId) => {
    setGrows(prev => prev.map(grow => {
      if (grow.id !== growId) return grow;
      return {
        ...grow,
        reminders: (grow.reminders || []).map(r =>
          r.id === reminderId ? { ...r, lastReset: new Date().toISOString() } : r
        ),
      };
    }));
  }, []);

  // Get reminders for a specific grow
  const getReminders = React.useCallback((growId) => {
    const grow = grows.find(g => g.id === growId);
    return grow?.reminders || [];
  }, [grows]);

  // ==================== GETTERS ====================

  // Get active grow object
  const getActiveGrow = React.useCallback(() => {
    if (!activeGrowId) return null;
    return grows.find(g => g.id === activeGrowId) || null;
  }, [activeGrowId, grows]);

  // Get preset for a specific grow
  const getPresetForGrow = React.useCallback((growId) => {
    const grow = grows.find(g => g.id === growId);
    if (!grow) return null;
    return presets.find(p => p.id === grow.presetId) || null;
  }, [grows, presets]);

  // Get active preset (for active grow)
  const getActivePreset = React.useCallback(() => {
    const activeGrow = getActiveGrow();
    if (!activeGrow) return null;
    return presets.find(p => p.id === activeGrow.presetId) || null;
  }, [getActiveGrow, presets]);

  // Get optimal ranges for active grow's current stage
  const getActiveOptimalRanges = React.useCallback(() => {
    const activeGrow = getActiveGrow();
    const preset = getActivePreset();
    if (!preset || !activeGrow?.currentStage) {
      return null;
    }
    return preset.stages[activeGrow.currentStage]?.sensors || null;
  }, [getActiveGrow, getActivePreset]);

  // Get optimal range for a specific sensor (from active grow)
  const getOptimalRange = React.useCallback((sensorType) => {
    const ranges = getActiveOptimalRanges();
    if (ranges && ranges[sensorType]) {
      return ranges[sensorType];
    }
    return null;
  }, [getActiveOptimalRanges]);

  // Calculate progress for a specific grow
  const getGrowProgress = React.useCallback((growId) => {
    const grow = growId ? grows.find(g => g.id === growId) : getActiveGrow();
    if (!grow) return null;

    const preset = presets.find(p => p.id === grow.presetId);
    if (!preset) return null;

    const startDate = new Date(grow.startDate);
    // For completed grows, use the end date; otherwise use now
    const endDate = grow.status === 'completed' && grow.endDate ? new Date(grow.endDate) : new Date();

    const currentStageIndex = STAGE_ORDER.indexOf(grow.currentStage);

    // Calculate total grow duration
    let totalDuration = 0;
    STAGE_ORDER.forEach((stage) => {
      totalDuration += preset.stages[stage]?.duration || 0;
    });

    // Calculate total days from start date
    const totalDaysFromStart = Math.max(0, Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)));

    // Calculate days in the current stage based on start date
    let daysBeforeCurrentStage = 0;
    for (let i = 0; i < currentStageIndex; i++) {
      daysBeforeCurrentStage += preset.stages[STAGE_ORDER[i]]?.duration || 0;
    }
    const daysInCurrentStage = Math.max(0, totalDaysFromStart - daysBeforeCurrentStage);

    const currentStageDuration = preset.stages[grow.currentStage]?.duration || 0;
    const currentStageProgress = currentStageDuration > 0
      ? Math.min(daysInCurrentStage / currentStageDuration, 1)
      : 0;

    const overallProgress = totalDuration > 0
      ? Math.min(totalDaysFromStart / totalDuration, 1)
      : 0;

    return {
      grow,
      preset,
      currentStage: grow.currentStage,
      currentStageIndex,
      daysInCurrentStage,
      currentStageDuration,
      currentStageProgress,
      totalDaysFromStart,
      totalDuration,
      overallProgress,
      stageOrder: STAGE_ORDER,
      stages: STAGE_ORDER.map(stage => ({
        id: stage,
        label: GROW_STAGES[stage].label,
        duration: preset.stages[stage]?.duration || 0,
      })),
    };
  }, [grows, presets, getActiveGrow]);

  // Auto-complete grows that have reached 100% progress
  // Use a ref to track which grows we've already auto-completed to prevent loops
  const autoCompletedRef = React.useRef(new Set());

  React.useEffect(() => {
    const growsToComplete = [];

    grows.forEach(grow => {
      // Skip if already processed or not active
      if (grow.status !== 'active' || autoCompletedRef.current.has(grow.id)) {
        return;
      }

      // Calculate progress inline to avoid dependency issues
      const preset = presets.find(p => p.id === grow.presetId);
      if (!preset) return;

      const startDate = new Date(grow.startDate);
      const now = new Date();

      let totalDuration = 0;
      ['seedling', 'vegetative', 'flower', 'lateFlower'].forEach(stage => {
        totalDuration += preset.stages[stage]?.duration || 0;
      });

      const totalDaysFromStart = Math.max(0, Math.floor((now - startDate) / (1000 * 60 * 60 * 24)));
      const overallProgress = totalDuration > 0 ? totalDaysFromStart / totalDuration : 0;

      if (overallProgress >= 1) {
        growsToComplete.push(grow.id);
      }
    });

    if (growsToComplete.length > 0) {
      // Mark as processed before updating
      growsToComplete.forEach(id => autoCompletedRef.current.add(id));

      setGrows(prev => prev.map(g =>
        growsToComplete.includes(g.id)
          ? {
              ...g,
              status: 'completed',
              endDate: new Date().toISOString(),
              currentStage: 'lateFlower',
            }
          : g
      ));
    }
  }, [grows, presets]);

  // Legacy compatibility: get activeGrow object (for existing components)
  const activeGrow = React.useMemo(() => getActiveGrow(), [getActiveGrow]);

  const value = React.useMemo(() => ({
    // State
    presets,
    grows,
    activeGrowId,
    activeGrow, // Legacy compatibility

    // Preset methods
    addPreset,
    updatePreset,
    deletePreset,
    duplicatePreset,
    copyStageSettings,
    updateStageSettings,
    updateSensorRange,

    // Grow methods
    addGrow,
    updateGrow,
    deleteGrow,
    endGrow,
    updateGrowStage,
    setActiveGrowId,
    updateGrowDashboardConfig,
    getGrowDashboardConfig,

    // Reminder methods
    addReminder,
    updateReminder,
    deleteReminder,
    resetReminder,
    getReminders,

    // Getters
    getActiveGrow,
    getPresetForGrow,
    getActivePreset,
    getActiveOptimalRanges,
    getOptimalRange,
    getGrowProgress,
  }), [
    presets,
    grows,
    activeGrowId,
    activeGrow,
    addPreset,
    updatePreset,
    deletePreset,
    duplicatePreset,
    copyStageSettings,
    updateStageSettings,
    updateSensorRange,
    addGrow,
    updateGrow,
    deleteGrow,
    endGrow,
    updateGrowStage,
    updateGrowDashboardConfig,
    getGrowDashboardConfig,
    addReminder,
    updateReminder,
    deleteReminder,
    resetReminder,
    getReminders,
    getActiveGrow,
    getPresetForGrow,
    getActivePreset,
    getActiveOptimalRanges,
    getOptimalRange,
    getGrowProgress,
  ]);

  return (
    <GrowContext.Provider value={value}>
      {children}
    </GrowContext.Provider>
  );
}

GrowProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useGrow() {
  const context = React.useContext(GrowContext);
  if (!context) {
    throw new Error('useGrow must be used within a GrowProvider');
  }
  return context;
}

export default GrowContext;
